/**
 * @page TournamentDetailPage
 * @description 賽事詳情 + 操作中樞（UI-2-3）
 * 依狀態顯示不同操作：報名、開賽、計時器、淘汰
 */

import TimedPricingControls from './TimedPricingControls';
import AssignTournamentModal from '../display/AssignTournamentModal';
import { BrandIcon } from '../../components/layout/BrandIcon';
import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TournamentDto, PlayerEntryDto } from '@pokerroom/types';
import { isAxiosError } from 'axios';
import { api } from '../../lib/api.ts';
import { useDialogFocus } from '../../hooks/useDialogFocus';
import { formatAmount, TournamentStatusBadge } from './tournamentDisplay';

interface RebuyAddonLog {
  id: string;
  type: 'REBUY' | 'ADDON' | 'REENTRY';
  entryId: string;
  isReverted?: boolean;
}

interface EliminationLog {
  id: string;
  entryId: string;
  rank: number;
  isReverted?: boolean;
}

const ACTION_BASE =
  'inline-flex min-h-11 items-center justify-center gap-2 px-4 text-sm font-medium transition-colors disabled:cursor-not-allowed';
const PRIMARY_ACTION = `${ACTION_BASE} bg-primary text-primary-content hover:bg-primary/90 disabled:bg-base-300 disabled:text-base-content/70`;
const OUTLINE_ACTION = `${ACTION_BASE} border border-base-300 text-base-content hover:bg-base-300 disabled:text-base-content/50`;
const DANGER_ACTION = `${ACTION_BASE} border border-error text-error hover:bg-error/10`;

async function fetchTournament(id: string): Promise<TournamentDto & { entries: PlayerEntryDto[] }> {
  return api.get<TournamentDto & { entries: PlayerEntryDto[] }>(`/tournament/${id}`);
}

async function fetchRebuyAddons(id: string): Promise<RebuyAddonLog[]> {
  return api.get<RebuyAddonLog[]>(`/tournament/${id}/rebuy-addons`);
}

async function fetchEliminations(id: string): Promise<EliminationLog[]> {
  return api.get<EliminationLog[]>(`/tournament/${id}/eliminations`);
}

async function startTournament(id: string) {
  return api.post(`/tournament/${id}/start`);
}

async function cancelTournament(id: string) {
  return api.post(`/tournament/${id}/cancel`);
}

export default function TournamentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation('tournament');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showAssign, setShowAssign] = useState(false);

  const { data: tournament, isLoading } = useQuery({
    queryKey: ['tournament', id],
    queryFn: () => fetchTournament(id!),
    enabled: !!id,
    refetchInterval: 5000,
  });

  const { data: rebuyAddonLogs = [] } = useQuery({
    queryKey: ['tournament', id, 'rebuy-addons'],
    queryFn: () => fetchRebuyAddons(id!),
    enabled:
      !!id &&
      (tournament?.status === 'IN_PROGRESS' ||
        tournament?.status === 'PAUSED' ||
        tournament?.status === 'COMPLETED'),
    refetchInterval: 5000,
  });

  const startMutation = useMutation({
    mutationFn: () => startTournament(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tournament', id] });
      navigate(`/tournament/${id}/timer`);
    },
    onError: (err: Error) => setActionError(err.message),
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelTournament(id!),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['tournament', id] }),
    onError: (err: Error) =>
      setActionError(
        isAxiosError(err) && typeof err.response?.data?.message === 'string'
          ? err.response.data.message
          : err.message,
      ),
  });

  const { data: eliminationLogs = [] } = useQuery({
    queryKey: ['tournament', id, 'eliminations'],
    queryFn: () => fetchEliminations(id!),
    enabled: !!id && (tournament?.status === 'IN_PROGRESS' || tournament?.status === 'PAUSED'),
    refetchInterval: 5000,
  });

  const rebuyAddonMutation = useMutation({
    mutationFn: (dto: { entryId: string; type: 'REBUY' | 'ADDON' }) =>
      api.post(`/tournament/${id}/rebuy-addon`, dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tournament', id] });
      void queryClient.invalidateQueries({ queryKey: ['tournament', id, 'rebuy-addons'] });
    },
    onError: (err: Error) => setActionError(err.message),
  });

  const undoEliminationMutation = useMutation({
    mutationFn: (logId?: string) =>
      api.post(`/tournament/${id}/undo-elimination`, logId ? { logId } : {}),
    onSuccess: () => {
      setActionError('');
      void queryClient.invalidateQueries({ queryKey: ['tournament', id] });
      void queryClient.invalidateQueries({ queryKey: ['tournament', id, 'eliminations'] });
    },
    onError: (err: Error) => setActionError(err.message),
  });

  const undoRebuyAddonMutation = useMutation({
    mutationFn: (logId?: string) =>
      api.post(`/tournament/${id}/undo-rebuy-addon`, logId ? { logId } : {}),
    onSuccess: () => {
      setActionError('');
      void queryClient.invalidateQueries({ queryKey: ['tournament', id] });
      void queryClient.invalidateQueries({ queryKey: ['tournament', id, 'rebuy-addons'] });
    },
    onError: (err: Error) => setActionError(err.message),
  });

  const finalizePlayMutation = useMutation({
    mutationFn: () => api.post(`/tournament/${id}/finalize-play`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['tournament', id] }),
    onError: (err: Error) => setActionError(err.message),
  });

  const manualFixMutation = useMutation({
    mutationFn: (dto: { currentLevel?: number; remainingSeconds?: number; reason: string }) =>
      api.patch(`/tournament/${id}/manual-fix`, dto),
    onSuccess: () => {
      setActionError('');
      setShowManualFix(false);
      void queryClient.invalidateQueries({ queryKey: ['tournament', id] });
    },
    onError: (err: Error) => setActionError(err.message),
  });

  const [expandedEntry, setExpandedEntry] = useState<string | null>(null);
  const [showManualFix, setShowManualFix] = useState(false);
  const [actionError, setActionError] = useState('');

  if (isLoading)
    return (
      <p role="status" className="p-6 text-base-content/70">
        {t('loading')}
      </p>
    );
  if (!tournament)
    return (
      <p role="alert" className="p-6 text-error">
        {t('notFound')}
      </p>
    );

  const entries = tournament.entries ?? [];
  const checkedInCount = entries.filter(
    (e) => e.status === 'CHECKED_IN' || e.status === 'PLAYING' || e.status === 'FINAL_TABLE',
  ).length;
  // 手動計數模式沒有 PlayerEntry，所有 entry 衍生的數字都會是 0。
  // 開賽與否要看後端算好的 liveStats，不能看 checkedInCount，
  // 否則手動賽事的開賽鍵永遠是禁用的。
  const liveStats = tournament.liveStats;
  const isManualCount = liveStats?.manualCountMode ?? tournament.manualCountMode ?? false;
  const startablePlayerCount = isManualCount ? (liveStats?.totalEntries ?? 0) : checkedInCount;
  // 計數器住在時鐘頁，所以手動賽事在開賽前就得進得去。
  const canOpenTimer =
    tournament.status === 'IN_PROGRESS' ||
    tournament.status === 'PAUSED' ||
    (isManualCount && (tournament.status === 'DRAFT' || tournament.status === 'REGISTERED'));
  const rebuyCount = rebuyAddonLogs.filter((l) => l.type === 'REBUY' && !l.isReverted).length;
  const reentryCount = rebuyAddonLogs.filter((l) => l.type === 'REENTRY' && !l.isReverted).length;
  const addonCount = rebuyAddonLogs.filter((l) => l.type === 'ADDON' && !l.isReverted).length;
  const preStart = tournament.status === 'DRAFT' || tournament.status === 'REGISTERED';
  const inPlay = tournament.status === 'IN_PROGRESS' || tournament.status === 'PAUSED';
  const isActive = inPlay || tournament.status === 'COMPLETED';
  const ts = tournament.timerState as Record<string, unknown> | null;
  const isSettling = !!ts?.settling;
  const timedSettlement = isManualCount && tournament.type === 'TIMED';
  const hasSettlementLink = !isManualCount || tournament.type === 'TIMED';
  const settlementTo = timedSettlement
    ? `/tournament/${id}/timed-settlement`
    : `/tournament/${id}/payout`;
  const settlementLabel = timedSettlement ? t('timedSettlement') : t('payout.title');
  // While settling, settlement is the task staff come for: it leads the page as the one primary action.
  const settlementLeads = isActive && isSettling && hasSettlementLink;
  const locale = i18n.language;

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      {showAssign && (
        <AssignTournamentModal
          key={`assign-${id}`}
          tournament={tournament}
          onClose={() => setShowAssign(false)}
        />
      )}
      {actionError && !showManualFix && (
        <div
          role="alert"
          className="mb-4 flex items-center justify-between gap-3 border border-error bg-error/10 pl-3 text-sm text-base-content"
        >
          <span className="py-2">{actionError}</span>
          <button
            type="button"
            onClick={() => setActionError('')}
            aria-label={t('common:close')}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center text-error hover:bg-error/10"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 16 16"
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>
      )}
      <TimedPricingControls key={tournament.id} tournament={tournament} />
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between mb-6">
        <div className="min-w-0">
          <Link
            to="/tournament"
            className="inline-flex min-h-11 items-center text-sm text-base-content/70 hover:text-base-content"
          >
            {t('backToList')}
          </Link>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <h1 className="break-words text-xl sm:text-2xl font-bold text-base-content">
              {tournament.name}
            </h1>
            <TournamentStatusBadge status={tournament.status} />
          </div>
          <p className="mt-2 text-sm text-base-content/70">
            {tournament.template?.name
              ? t('templateSource', { name: tournament.template.name })
              : tournament.templateId
                ? t('templateMissing')
                : t('templateNone')}
          </p>
        </div>

        {/* 快速操作：每個狀態只有一個主要操作 */}
        {(preStart || canOpenTimer || inPlay) && (
          <div className="flex flex-col gap-2 lg:items-end">
            <div className="flex flex-wrap gap-2">
              {preStart && (
                <button
                  type="button"
                  onClick={() => startMutation.mutate()}
                  disabled={startMutation.isPending || startablePlayerCount === 0}
                  className={PRIMARY_ACTION}
                >
                  {startMutation.isPending ? t('starting') : t('start')}
                </button>
              )}
              {canOpenTimer && (
                <Link
                  to={`/tournament/${id}/timer`}
                  className={inPlay && !settlementLeads ? PRIMARY_ACTION : OUTLINE_ACTION}
                >
                  <BrandIcon value="timer" size={20} />
                  {t('timer.control')}
                </Link>
              )}
              <button type="button" onClick={() => setShowAssign(true)} className={OUTLINE_ACTION}>
                <BrandIcon value="tv" size={20} />
                {t('assignDisplay')}
              </button>
              {inPlay && (
                <button
                  type="button"
                  onClick={() => {
                    setActionError('');
                    setShowManualFix(true);
                  }}
                  className={OUTLINE_ACTION}
                >
                  <BrandIcon value="build" size={20} />
                  {t('manualFix')}
                </button>
              )}
            </div>
            {preStart && startablePlayerCount === 0 && (
              <p className="text-sm text-base-content/70">{t('startNeedsPlayers')}</p>
            )}
          </div>
        )}
      </div>

      {isActive && isSettling && (
        <section
          role="status"
          aria-labelledby="settling-title"
          className="mb-6 border-2 border-warning bg-warning/10 p-4 sm:p-5"
        >
          <h2 id="settling-title" className="text-lg font-bold text-base-content">
            {t('settlingTitle')}
          </h2>
          <p className="mt-1 text-sm text-base-content">{t('settlingMode')}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {settlementLeads && (
              <Link to={settlementTo} className={PRIMARY_ACTION}>
                {settlementLabel}
              </Link>
            )}
            <Link to="/tables" className={OUTLINE_ACTION}>
              {t('goToTables')}
            </Link>
          </div>
        </section>
      )}

      {/* 賽事概覽 */}
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 mb-6">
        {[
          {
            label: t('overview.prizePool'),
            value: formatAmount(String(tournament.prizePool), locale),
          },
          { label: t('overview.entries'), value: entries.length + reentryCount },
          { label: t('overview.rebuys'), value: rebuyCount },
          { label: t('overview.addons'), value: addonCount },
        ].map(({ label, value }) => (
          <div key={label} className="bg-base-200 border border-base-300 p-3 sm:p-4 text-center">
            <dd className="text-2xl font-bold text-base-content tabular-nums">{value}</dd>
            <dt className="text-xs text-base-content/70 mt-1">{label}</dt>
          </div>
        ))}
      </dl>

      {tournament.overlayAmount && Number(tournament.overlayAmount) > 0 && (
        <p className="border border-warning bg-warning/10 p-4 mb-6 text-sm text-base-content">
          {t('overlayNote', { amount: formatAmount(String(tournament.overlayAmount), locale) })}
        </p>
      )}

      {/* 報名 / 玩家管理 */}
      <section className="bg-base-200 border border-base-300 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-base-300">
          <h2 className="text-base-content font-medium">
            {t('playerSummary', {
              checkedIn: checkedInCount,
              total: entries.length,
              rebuys: rebuyCount,
              addons: addonCount,
            })}
          </h2>
          {(preStart || tournament.status === 'IN_PROGRESS') && (
            <Link to={`/tournament/${id}/register`} className={OUTLINE_ACTION}>
              {t('register')}
            </Link>
          )}
        </div>

        {entries.length === 0 ? (
          <p className="p-8 text-center text-base-content/70">{t('noEntries')}</p>
        ) : (
          <ul className="divide-y divide-base-300">
            {entries.map((entry) => {
              const entryRebuys = rebuyAddonLogs.filter(
                (l) => l.entryId === entry.id && l.type === 'REBUY' && !l.isReverted,
              ).length;
              const entryAddons = rebuyAddonLogs.filter(
                (l) => l.entryId === entry.id && l.type === 'ADDON' && !l.isReverted,
              ).length;
              const isPlaying = entry.status === 'PLAYING' || entry.status === 'FINAL_TABLE';
              const showActions = isPlaying && inPlay;
              const isExpanded = expandedEntry === entry.id;
              const rowContent = (
                <>
                  <span className="min-w-0">
                    <span className="text-base-content font-medium">{entry.playerName}</span>
                    {entry.tableNo && (
                      <span className="text-base-content/70 text-sm ml-3 tabular-nums">
                        {t('tableSeat', { table: entry.tableNo, seat: entry.seatNo })}
                      </span>
                    )}
                    {(entryRebuys > 0 || entryAddons > 0) && (
                      <span className="text-base-content/70 text-xs ml-2 tabular-nums">
                        {entryRebuys > 0 && `R${entryRebuys}`}
                        {entryRebuys > 0 && entryAddons > 0 && ' '}
                        {entryAddons > 0 && `A${entryAddons}`}
                      </span>
                    )}
                  </span>
                  <span className="flex shrink-0 items-center gap-3">
                    {entry.finalRank && (
                      <span className="text-base-content/70 text-sm tabular-nums">
                        #{entry.finalRank}
                      </span>
                    )}
                    <span
                      className={`inline-flex h-6 items-center px-2 text-xs font-medium ${
                        isPlaying
                          ? 'bg-success/10 text-success'
                          : entry.status === 'ELIMINATED'
                            ? 'border border-error text-error'
                            : 'bg-base-300 text-base-content'
                      }`}
                    >
                      {t(`playerStatus.${entry.status}`)}
                    </span>
                  </span>
                </>
              );

              return (
                <li key={entry.id}>
                  {showActions ? (
                    <button
                      type="button"
                      aria-expanded={isExpanded}
                      aria-controls={`entry-actions-${entry.id}`}
                      onClick={() => setExpandedEntry(isExpanded ? null : entry.id)}
                      className="flex w-full min-h-11 items-center justify-between gap-3 px-4 py-3 text-left hover:bg-base-300 transition-colors"
                    >
                      {rowContent}
                    </button>
                  ) : (
                    <div className="flex items-center justify-between gap-3 px-4 py-3">
                      {rowContent}
                    </div>
                  )}
                  {/* Rebuy / Addon 操作列 */}
                  {showActions && isExpanded && (
                    <div id={`entry-actions-${entry.id}`} className="flex gap-2 px-4 pb-3">
                      {tournament.rebuyAllowed && (
                        <button
                          type="button"
                          onClick={() =>
                            rebuyAddonMutation.mutate({ entryId: entry.id, type: 'REBUY' })
                          }
                          disabled={rebuyAddonMutation.isPending}
                          className={OUTLINE_ACTION}
                        >
                          {t('rebuy')}
                        </button>
                      )}
                      {tournament.addonAllowed && (
                        <button
                          type="button"
                          onClick={() =>
                            rebuyAddonMutation.mutate({ entryId: entry.id, type: 'ADDON' })
                          }
                          disabled={rebuyAddonMutation.isPending}
                          className={OUTLINE_ACTION}
                        >
                          {t('addon')}
                        </button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* 賽事操作 */}
      {isActive && (
        <div className="flex flex-wrap gap-2">
          <Link to={`/tournament/${id}/eliminate`} className={OUTLINE_ACTION}>
            {t('eliminate')}
          </Link>
          {!isSettling && inPlay && (
            <button
              type="button"
              onClick={() => {
                if (confirm(t('confirmFinalizePlay'))) {
                  finalizePlayMutation.mutate();
                }
              }}
              disabled={finalizePlayMutation.isPending}
              className={OUTLINE_ACTION}
            >
              {t('finalizePlay')}
            </button>
          )}
          {hasSettlementLink && !settlementLeads && (
            <Link to={settlementTo} className={OUTLINE_ACTION}>
              {settlementLabel}
            </Link>
          )}
          <Link to={`/tournament/${id}/payout-rule`} className={OUTLINE_ACTION}>
            {t('payoutRuleLink')}
          </Link>
        </div>
      )}

      {tournament.status === 'CANCELLED' && (
        <div className="flex gap-2">
          <Link to={`/tournament/${id}/payout`} className={OUTLINE_ACTION}>
            {t('payout.title')}
          </Link>
        </div>
      )}

      {preStart && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              if (confirm(t('confirmCancelTournament'))) cancelMutation.mutate();
            }}
            className={DANGER_ACTION}
          >
            {t('cancel')}
          </button>
        </div>
      )}

      {/* 淘汰撤銷歷史 */}
      {isActive && eliminationLogs.length > 0 && (
        <section className="mt-6 bg-base-200 border border-base-300">
          <h2 className="p-4 border-b border-base-300 text-base-content font-medium">
            {t('eliminationHistory')}
          </h2>
          <ul className="divide-y divide-base-300">
            {eliminationLogs
              .filter((l) => !l.isReverted)
              .slice(0, 10)
              .map((log) => {
                const logEntry = entries.find((e) => e.id === log.entryId);
                return (
                  <li key={log.id} className="flex items-center justify-between gap-3 px-4 py-2">
                    <span>
                      <span className="text-base-content font-medium">
                        {logEntry?.playerName ?? log.entryId.slice(0, 8)}
                      </span>
                      <span className="text-base-content/70 text-sm ml-2 tabular-nums">
                        #{log.rank}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => undoEliminationMutation.mutate(String(log.id))}
                      disabled={undoEliminationMutation.isPending}
                      className={OUTLINE_ACTION}
                    >
                      {t('undoElimination')}
                    </button>
                  </li>
                );
              })}
          </ul>
        </section>
      )}

      {showManualFix && (
        <ManualFixDialog
          pending={manualFixMutation.isPending}
          error={actionError}
          onCancel={() => setShowManualFix(false)}
          onSubmit={(dto) => manualFixMutation.mutate(dto)}
        />
      )}
    </div>
  );
}

/** Emergency fix of the clock level / remaining seconds; the reason is required. */
export function ManualFixDialog({
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  pending: boolean;
  error?: string;
  onCancel: () => void;
  onSubmit: (dto: { currentLevel?: number; remainingSeconds?: number; reason: string }) => void;
}) {
  const { t } = useTranslation('tournament');
  const [fixReason, setFixReason] = useState('');
  const [fixLevel, setFixLevel] = useState('');
  const [fixSeconds, setFixSeconds] = useState('');
  const dialogRef = useDialogFocus(true);
  const blocked = pending || !fixReason;
  const field =
    'w-full min-h-11 px-3 bg-base-100 border border-base-300 text-base text-base-content';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="manual-fix-title"
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel();
        }}
        className="bg-base-200 p-6 max-w-md border border-base-300 w-full"
      >
        <h2 id="manual-fix-title" className="text-lg font-bold text-base-content mb-4">
          {t('manualFixTitle')}
        </h2>
        <div className="space-y-4">
          <div>
            <label
              htmlFor="manual-fix-level"
              className="block text-sm font-medium text-base-content mb-1"
            >
              {t('fixLevel')}
            </label>
            <input
              id="manual-fix-level"
              type="number"
              value={fixLevel}
              onChange={(e) => setFixLevel(e.target.value)}
              placeholder={t('fixKeepHint')}
              className={field}
            />
          </div>
          <div>
            <label
              htmlFor="manual-fix-seconds"
              className="block text-sm font-medium text-base-content mb-1"
            >
              {t('fixSeconds')}
            </label>
            <input
              id="manual-fix-seconds"
              type="number"
              value={fixSeconds}
              onChange={(e) => setFixSeconds(e.target.value)}
              placeholder={t('fixKeepHint')}
              className={field}
            />
          </div>
          <div>
            <label
              htmlFor="manual-fix-reason"
              className="block text-sm font-medium text-base-content mb-1"
            >
              {t('fixReason')} <span className="text-error">*</span>
            </label>
            <input
              id="manual-fix-reason"
              type="text"
              required
              value={fixReason}
              onChange={(e) => setFixReason(e.target.value)}
              className={field}
            />
          </div>
        </div>
        {error && (
          <p
            role="alert"
            className="mt-4 border border-error bg-error/10 p-3 text-sm text-base-content"
          >
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onCancel} className={OUTLINE_ACTION}>
            {t('common:cancel')}
          </button>
          <button
            type="button"
            onClick={() => {
              // Never natively disabled: a disabled focused button drops focus out of the dialog.
              if (blocked) return;
              onSubmit({
                ...(fixLevel ? { currentLevel: Number(fixLevel) } : {}),
                ...(fixSeconds ? { remainingSeconds: Number(fixSeconds) } : {}),
                reason: fixReason,
              });
            }}
            aria-disabled={blocked}
            className={`${ACTION_BASE} ${
              blocked
                ? `bg-base-300 text-base-content/70 ${pending ? 'cursor-wait' : 'cursor-not-allowed'}`
                : 'bg-primary text-primary-content hover:bg-primary/90'
            }`}
          >
            {pending ? t('fixSubmitting') : t('fixSubmit')}
          </button>
        </div>
      </div>
    </div>
  );
}
